import { BaseExplorerConfig, FieldTransformer } from '../../../types/baseExplorerConfig';
import { HotspotEntity } from '../../../types/hotspot';
import { hotspotServiceAdapter } from './hotspotServiceAdapter';
import { faFire } from '@fortawesome/free-solid-svg-icons';
import { AGE_CLASS_LABELS, SOURCE_LABELS } from '../../../shared/hotspotTransforms';
import { HOTSPOT_COLUMNS } from '../../../shared/hotspotSchema';
import { gridColumns } from '../../../shared/columnSchema';

const dateTimeTransformer: FieldTransformer = {
  transform: (value: string | null) => {
    if (!value) return 'N/A';
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleString('en-CA', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
        hour12: false, timeZoneName: 'short',
      });
    } catch { return value; }
  },
  reverseTransform: (value: any) => String(value || ''),
};

const brightnessTransformer: FieldTransformer = {
  transform: (value: number | null) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(1)} K`;
  },
  reverseTransform: (value: any) => {
    const str = String(value || '').replace(' K', '');
    return isNaN(Number(str)) ? 0 : Number(str);
  },
};

const frpTransformer: FieldTransformer = {
  transform: (value: number | null) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(1)} MW`;
  },
  reverseTransform: (value: any) => {
    const str = String(value || '').replace(' MW', '');
    return isNaN(Number(str)) ? 0 : Number(str);
  },
};

const ageClassTransformer: FieldTransformer = {
  transform: (value: string | null) => (value && AGE_CLASS_LABELS[value]) || value || 'Unknown',
  reverseTransform: (value: any) => String(value || ''),
};

const sourceTransformer: FieldTransformer = {
  transform: (value: string | null) => (value && SOURCE_LABELS[value]) || value || 'Unknown',
  reverseTransform: (value: any) => String(value || ''),
};

export const createHotspotConfig = (): BaseExplorerConfig<HotspotEntity> => ({
  entityName: 'Hotspot Detection',
  complexityLevel: 'moderate',
  autoRefreshIntervalMs: 300000,

  displayFields: [
    'acqDatetime', 'lat', 'lon', 'brightness', 'frp',
    'confidence', 'source', 'ageClass', 'scan', 'track',
  ],

  serviceAdapter: hotspotServiceAdapter,

  fieldTransformers: {
    acqDatetime: dateTimeTransformer,
    brightness: brightnessTransformer,
    frp: frpTransformer,
    ageClass: ageClassTransformer,
    source: sourceTransformer,
  } as any,

  features: {
    pagination: {
      enabled: true,
      defaultPageSize: 50,
      pageSizeOptions: [25, 50, 100, 200],
      showPageInfo: true,
    },
    sorting: {
      enabled: true,
      multiColumn: false,
      defaultSort: [{ field: 'acqDatetime', direction: 'desc' }],
    },
    searching: {
      enabled: false,
      placeholder: '',
      searchFields: [],
      debounceMs: 300,
    },
    filtering: {
      enabled: true,
      advancedFilters: false,
      quickFilters: [
        { field: 'acqDatetime', label: 'Detection Date', type: 'dateRange' },
        { field: 'brightness', label: 'Brightness (K)', type: 'numeric' },
        { field: 'frp', label: 'Fire Radiative Power (MW)', type: 'numeric' },
        {
          field: 'confidence',
          label: 'Confidence',
          type: 'dropdown',
          options: [
            { value: 'l', label: 'Low' },
            { value: 'n', label: 'Nominal' },
            { value: 'h', label: 'High' },
          ],
        },
        {
          field: 'source',
          label: 'Satellite',
          type: 'dropdown',
          options: [
            { value: 'noaa20', label: 'NOAA-20' },
            { value: 'noaa21', label: 'NOAA-21' },
            { value: 'snpp', label: 'Suomi-NPP' },
          ],
        },
        {
          field: 'ageClass',
          label: 'Age',
          type: 'dropdown',
          options: [
            { value: 'hot', label: 'Hot (<12h)' },
            { value: 'warm', label: 'Warm (12-24h)' },
            { value: 'cold', label: 'Cold (>24h)' },
          ],
        },
        { field: 'lat', label: 'Latitude', type: 'numeric' },
        { field: 'lon', label: 'Longitude', type: 'numeric' },
      ],
    },
    export: {
      enabled: true,
      formats: ['csv'],
      includeFilters: true,
      entity: 'hotspots',
    },
    editing: {
      enabled: false,
      allowCreate: false,
      allowUpdate: false,
      allowDelete: false,
      mode: 'modal',
    },
  },

  presentation: {
    title: 'Hotspot Detections',
    subtitle: 'VIIRS satellite fire detections',
    icon: faFire,
    columnConfig: gridColumns(HOTSPOT_COLUMNS),
    styling: {
      theme: 'default',
      striped: true,
      bordered: true,
      hoverable: true,
    },
    layout: {
      responsive: true,
      showFooter: true,
      showToolbar: true,
      minHeight: 400,
      maxHeight: 800,
    },
  },
});
